/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import {
  ClipboardButton,
  type CodeBlock,
  LoadingSpinner,
  StatusLabel,
  Typography,
  useNeedleTheme,
} from '@neo4j-ndl/react';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { PrismLight as SyntaxHighlighter } from 'react-syntax-highlighter';

import { type CommonProps } from '../../_common/types';
import {
  type LanguageProp,
  Languages,
  loadLanguage,
} from '../../code-block/code-languages';
import ndlCodeDark from '../../code-block/themes/ndl-code-dark';
import ndlCodeLight from '../../code-block/themes/ndl-code-light';

type CodePreviewProps = {
  /** The code content to display */
  code: string;
  /** The language of the code (e.g., 'cypher', 'python', 'javascript') */
  language: React.ComponentProps<typeof CodeBlock>['language'];
  /** The current state of the code block */
  label?: 'read' | 'write';
  /** Whether the code block is loading */
  isLoading?: boolean;
  /** The actions to display in the header. Should be small CleanIconButton components. */
  headerActions?: React.ReactNode;
  /** The theme of the code block */
  theme?: 'ndl-code-dark' | 'ndl-code-light';
};

/**
 * The component is used to display code that is generated by an LLM.
 *
 * @alpha - Changes to this component may be breaking.
 * @deprecated - Use <Preview /> with <Preview.Code /> instead.
 */
export const CodePreview = ({
  code,
  language,
  label,
  isLoading = false,
  headerActions,
  theme,
  ref,
  style,
  className,
  htmlAttributes,
  ...restProps
}: CommonProps<'div', CodePreviewProps>) => {
  const [loadedLanguage, setLoadedLanguage] = useState<
    CodePreviewProps['language'] | null
  >(null);
  const { theme: ndlTheme } = useNeedleTheme();

  useEffect(() => {
    if (!language || !(Languages as readonly string[]).includes(language)) {
      setLoadedLanguage('text');
      return;
    }

    loadLanguage(language as LanguageProp)
      .then((module) => {
        SyntaxHighlighter.registerLanguage(language, module.default);
        setLoadedLanguage(language);
      })
      .catch((err) => console.error(err));
  }, [language]);

  const getTheme = () => {
    switch (theme) {
      case 'ndl-code-dark':
        return ndlCodeDark;
      case 'ndl-code-light':
        return ndlCodeLight;
      default:
        return ndlTheme === 'light' ? ndlCodeLight : ndlCodeDark;
    }
  };

  return (
    <div
      ref={ref}
      style={style}
      className={classNames('ndl-ai-code-preview', className)}
      {...restProps}
      {...htmlAttributes}
    >
      {/* Header */}
      <div className="ndl-ai-code-preview-header">
        <div className="ndl-ai-code-preview-title">
          {isLoading ? (
            <>
              <LoadingSpinner size="medium" />
              <Typography variant="code">
                WRITING {language.toUpperCase()}
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="code">
                {language.toUpperCase()}{' '}
                {language.toLowerCase() === 'cypher' ? 'QUERY' : ''}
              </Typography>
              {label && (
                <StatusLabel
                  variant={label === 'write' ? 'warning' : 'info'}
                  size="small"
                  fill="semi-filled"
                >
                  {label}
                </StatusLabel>
              )}
            </>
          )}
        </div>

        {!isLoading && (
          <div className="ndl-ai-code-preview-actions">
            <ClipboardButton
              textToCopy={code}
              type="clean-icon-button"
              size="small"
            />
            {headerActions}
          </div>
        )}
      </div>

      {!isLoading && (
        <div className="ndl-ai-code-preview-content">
          <div className="ndl-code-content-container">
            <SyntaxHighlighter
              language={loadedLanguage ?? 'text'}
              style={{
                ...getTheme(),
                'pre[class*="language-"]': {
                  border: 0,
                  color: 'var(--theme-color-neutral-text-default)',
                  lineHeight: '1',
                  overflowX: 'auto',
                  padding: 'var(--space-12)',
                  width: 'fit-content',
                },
              }}
              // Turn on 'shouldShowLineNumbers' & 'wrapLongLines' at the same time, the display is wrong
              // https://github.com/react-syntax-highlighter/react-syntax-highlighter/issues/402
              // wrapLongLines
              codeTagProps={{ className: 'n-ai-code-preview' }}
              showLineNumbers={false}
            >
              {code}
            </SyntaxHighlighter>
          </div>
        </div>
      )}
    </div>
  );
};
